import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { PaymentDialog } from "@/components/PaymentDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet as WalletIcon, Plus, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { toast } from "sonner";

export default function Wallet() {
  const [kids, setKids] = useState([]);
  const [childId, setChildId] = useState("");
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [amount, setAmount] = useState("25");
  const [order, setOrder] = useState(null);

  useEffect(() => { api.get("/children").then((r) => { setKids(r.data); if (r.data[0]) setChildId(r.data[0].id); }); }, []);
  const load = () => {
    if (!childId) return;
    api.get(`/wallet/${childId}`).then((r) => setWallet(r.data));
    api.get(`/wallet/${childId}/transactions`).then((r) => setTxns(r.data));
  };
  useEffect(() => { load(); }, [childId]);

  const topup = async () => {
    try { const { data } = await api.post(`/wallet/${childId}/topup`, { amount: parseFloat(amount) }); setOrder(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveSettings = async (patch) => {
    const next = { snack_enabled: wallet.snack_enabled, daily_limit: wallet.daily_limit, low_balance_alerts: wallet.low_balance_alerts, ...patch };
    setWallet({ ...wallet, ...next });
    try { await api.put(`/wallet/${childId}/settings`, next); toast.success("Wallet settings updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><WalletIcon className="w-6 h-6 text-turquoise" /> Adventure Wallet</h1>
      <p className="text-slate-500">Cashless snacks — fully controlled by you.</p>

      <div className="mt-4 max-w-xs">
        <Select value={childId} onValueChange={setChildId}>
          <SelectTrigger className="rounded-xl" data-testid="wallet-child-select"><SelectValue placeholder="Select explorer" /></SelectTrigger>
          <SelectContent>{kids.map((k) => <SelectItem key={k.id} value={k.id}>{k.preferred_name || k.first_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {wallet && (
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-navy to-ocean text-white rounded-2xl p-6" data-testid="wallet-balance-card">
              <p className="text-white/60 text-sm">Current balance</p>
              <p className="font-display font-black text-4xl text-gold">${Number(wallet.balance).toFixed(2)}</p>
              <div className="flex gap-2 mt-4 flex-wrap">
                {[10, 20, 30, 50].map((v) => (
                  <button key={v} onClick={() => setAmount(String(v))} data-testid={`preset-${v}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold border transition-colors ${String(v) === amount ? "bg-gold text-navy border-gold" : "bg-white/10 text-white border-white/20 hover:border-gold"}`}>${v}</button>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" className="rounded-xl bg-white/10 border-white/20 text-white w-28" data-testid="topup-amount" />
                <Button onClick={topup} className="bg-gold text-navy font-bold rounded-full" data-testid="topup-btn"><Plus className="w-4 h-4 mr-1" /> Add Funds</Button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div><p className="font-semibold text-navy text-sm">Snack purchasing</p><p className="text-xs text-slate-400">Allow purchases at the snack bar</p></div>
                <Switch checked={wallet.snack_enabled} onCheckedChange={(v) => saveSettings({ snack_enabled: v })} data-testid="snack-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <div><p className="font-semibold text-navy text-sm">Low-balance alerts</p><p className="text-xs text-slate-400">Notify me when balance is low</p></div>
                <Switch checked={wallet.low_balance_alerts} onCheckedChange={(v) => saveSettings({ low_balance_alerts: v })} data-testid="lowbal-toggle" />
              </div>
              <div>
                <Label className="text-xs">Daily spending limit ($, optional)</Label>
                <Input type="number" defaultValue={wallet.daily_limit || ""} onBlur={(e) => saveSettings({ daily_limit: e.target.value ? parseFloat(e.target.value) : null })} className="rounded-xl mt-1" data-testid="daily-limit-input" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="font-display font-bold text-navy mb-3">Transaction History</p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {txns.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b border-slate-100" data-testid={`txn-${t.id}`}>
                  <div className="flex items-center gap-2">
                    {t.type === "deposit" ? <ArrowDownLeft className="w-4 h-4 text-emerald-500" /> : <ArrowUpRight className="w-4 h-4 text-coral" />}
                    <div><p className="text-sm font-medium text-navy">{t.item}</p><p className="text-[11px] text-slate-400">{new Date(t.timestamp).toLocaleString()}</p></div>
                  </div>
                  <span className={`font-bold text-sm ${t.type === "deposit" ? "text-emerald-500" : "text-coral"}`}>{t.type === "deposit" ? "+" : "−"}${t.amount.toFixed(2)}</span>
                </div>
              ))}
              {txns.length === 0 && <p className="text-slate-400 text-sm">No transactions yet.</p>}
            </div>
          </div>
        </div>
      )}

      <PaymentDialog order={order} open={!!order} onClose={() => setOrder(null)} onDone={load} />
    </div>
  );
}
